import { createContext, useContext, useMemo } from 'react'

import { useApp } from '../../../app/AppContext.jsx'

import { FETCH_STATUS } from '../../../shared/constants/fetchStatus.js'

import { useBuildings } from '../hooks/useBuildings.js'
import { useCompartments } from '../hooks/useCompartments.js'
import { useInteriors } from '../hooks/useInteriors.js'
import { useProducts } from '../hooks/useProducts.js'
import { buildBuildingsData } from '../utils/buildBuildingsData.js'
import { buildTiersData } from '../utils/buildTiersData.js'
import { deriveInventoryCapabilities } from '../utils/deriveInventoryCapabilities.js'
import { isDisplayable } from '../../../shared/utils/productUtils.js'

import { COLOR_MODE, FALLBACK_COLOR_MODE }      from '../../../shared/constants/colorMode.js'
import { useColorMode } from '../../catalog/hooks/useColorMode.js'
import { buildProductAreaDeciles } from '../../../shared/utils/productAreaDeciles.js'


export const InventoryContext = createContext(null)

const EMPTY_SETTINGS = Object.freeze([])
const DEFAULT_PRODUCT_CATEGORY_KEYS = Object.freeze(['apartments'])

// ── Helpers ─────────────────────────────────────────────────────────────────────

/**
 * Transforms raw panelSettings model_color_modes into clean, normalized UI capabilities.
 * Sorts by `order` and normalizes property names to camelCase.
 *
 * @param {object|null|undefined} panelSettings - Configuration object from panel-settings.json
 * @returns {{ activeColorModes: Array<{ id: string }>, defaultColorMode: string }}
 */
function deriveColorModes(panelSettings) {
  const rawModes = panelSettings?.model_color_modes?.modes

  if (!Array.isArray(rawModes)) {
    return {
      activeColorModes: [],
      defaultColorMode: FALLBACK_COLOR_MODE,
    }
  }

  const activeColorModes = rawModes
    .filter((mode) => Boolean(mode?.is_active) && Boolean(mode?.id))
    .sort((a, b) => Number(a?.order ?? 0) - Number(b?.order ?? 0))
    .map((mode) => ({
      id: mode.id,
    }))

  const defaultColorMode = activeColorModes[0]?.id ?? FALLBACK_COLOR_MODE

  return {
    activeColorModes,
    defaultColorMode,
  }
}

/**
 * 
 */
function deriveInventoryStatus({
  buildingsStatus,
  compartmentsStatus,
  interiorsStatus,
  productsStatus,
}) {
  const requiredStatuses = [
    productsStatus,
    buildingsStatus,
  ]

  if (requiredStatuses.includes(FETCH_STATUS.ERROR)) {
    return {
      status: FETCH_STATUS.ERROR,
      isDegraded: false,
    }
  }

  if (requiredStatuses.includes(FETCH_STATUS.LOADING)) {
    return {
      status: FETCH_STATUS.LOADING,
      isDegraded: false,
    }
  }

  const isDegraded =
    compartmentsStatus === FETCH_STATUS.ERROR ||
    interiorsStatus === FETCH_STATUS.ERROR

  return {
    status: FETCH_STATUS.READY,
    isDegraded,
  }
}

// ── Provider ──────────────────────────────────────────────
/**
 * Global products data context provider.
 */
export function InventoryContextProvider({ children }) {
  const {
    panelSettings,
    projectConfig,
    windowData,
  } = useApp();
  
	// ── Resolved panel settings and project config derived flags and context ─────────────────────
  const dataUrl = windowData?.projectDataUrl;
  const projectId = windowData?.projectId;
  const siteUrl = windowData?.siteUrl;

  // ── project config derived  
  const appConfig = projectConfig?.app_config ?? {};
  const productCategoryKeys =
    projectConfig?.crm_integration?.product_categories_mapping?.application_main_products
    ?? DEFAULT_PRODUCT_CATEGORY_KEYS
  const interiorsCommissioned =
    appConfig?.interiors?.render_ai?.enabled === true
  const commissioning = useMemo(
    () => ({
      insolationCommissioned:
        appConfig?.apartments?.floorplan_insolation === true,

      compartmentVariantsCommissioned:
        appConfig?.apartments?.compartments_versioning === true,

      axonometryCommissioned:
        appConfig?.apartments?.render_360?.enabled === true,

      interiorsCommissioned:
        appConfig?.interiors?.render_ai?.enabled === true,
    }),
    [projectConfig],
  )


    // ── panel settings derived  
  const phasingConfig = panelSettings?.phasing_config?.buildings ?? EMPTY_SETTINGS
	const tiersSettings = panelSettings?.tiers_settings?.tiers ?? EMPTY_SETTINGS
	//TODO: tiers with flag !is_active shouldn't be included in filters range?


  // ── External Data Resources ──────────────────────────────────────────────
  const buildingsResource = useBuildings({dataUrl})
  const compartmentsResource = useCompartments({dataUrl})
  const interiorsResource = useInteriors({
    dataUrl,
    enabled: interiorsCommissioned,
  })
  const productsResource = useProducts({
    dataUrl,
    productCategoryKeys,
    projectId,
    siteUrl
  })
  // ── Products ──────────────────────────────────────────────
  const allProducts = productsResource.data;
  const displayableProducts = useMemo(
    () =>
      allProducts.filter(product =>
        isDisplayable(product.visibility),
      ),
    [allProducts],
  )
  // ── Tiers ──────────────────────────────────────────────
  const tiers = useMemo(
    () => buildTiersData(
      buildingsResource.data, 
      productsResource.data, //not displayableProducts
      tiersSettings),
    [
      buildingsResource, 
      productsResource.data, 
      tiersSettings
    ],
  );
  // ── Buildings ──────────────────────────────────────────────
  const enhancedBuildings = useMemo(
    () => buildBuildingsData(buildingsResource.data, tiers, phasingConfig),
    [buildingsResource, tiers, phasingConfig],
  );


	/*
    LIST ALL POSSIBLE CAPABILITIES TO BE MIGRATED HERE:
    app/
        activeColorModes,
    defaultColorMode,
    hasColorModes,
    heroPaddingEnabled,
    // ZOSTAJE W APP
    developerDisplayName,
    favoritesSectionEnabled,
    heroSectionEnabled,
    highlightSoldInRed,
    productSectionEnabled,
    reservedUnitsAccessible,
    reservedUnitsPlanVisible,
    showPlanThumb,
    showThreeDThumb,
    soldUnitsAccessible,
    soldUnitsPlanVisible,
    tiersSectionEnabled,
    
    hero /
        availableViews,
    buildingFrameStart,
    buildingFramesCount,
    buildingOutlineLayers,
    hasBuildingModel,
    hasHeroSection,
    hasSiteModel,
    hasVisualization,
    highlightSoldInRed, //TODO: move this to AppCapabilites as its used by both Hero and Tiers domain
    initialOutline,
    initialView,
    siteFrameStart,
    siteFramesCount,
    siteOutlineLayers,
    visOutlineLayers,
    
    catalog/
          areSoldOutBuildingsActive,
    areSoldOutTiersActive,
    ...catalogDisplay,
    ...catalogFilters,
    catalogFilters, //TODO: move Filters to use directly mainFilters, promotedFilters, additionalFilters,
    catalogSortingParameters,
    defaultProductCategory,
    hasArrangeCommissioned,
    hasAxonometricCommissioned,
    hasHotspotCommissioned,
    hasInsolationCommissioned,
    hasMultipleAreas,
    hasMultipleBuildings,
    hasMultipleGates,
    hasMultipleTierSets,
    listViewStickyColumnsCount,
    loadWithExpandedFilters,
    loadViewModeDesktop,
    loadViewModeMobile,
    pricesReservedVisible,
    pricesSoldVisible,
    pricesVisible,
    		getAdjacentProducts,
    		colorMode,
        setColorMode,
        availableColorModes,
        colorModeDeciles,
        activeFiltersConfig,
        activeFiltersCount,
        canRestorePreReset: preResetSnapshot !== null,
        catalogProducts,
        defaultFiltersConfig,
        filteredProducts,
        fullFiltersRange,
        setActiveFiltersConfig,
        setSortConfig,
        sortConfig,
        resetFilters,
        resetSortConfig,
        restorePreReset,

    product/
        hasViewer: false,
    hasCompartments: false,
    hasPricing: false,
    hasPriceHistory: false,
    hasSupplements: false,
    hasMonetaryObligations: false,
    hasRelatedProducts: false,
    hasResources: false,
    isMultiLevel: false,
    supportsLevelSelector: false,
    availableLevels: [],
    defaultLevel: null,
    hasAxonometricCamera: false,
    hasHotspotCameras: false,
    hasInsolationData: false,
    hasCompartmentVariants: false,
    allowsThreeDVariant: false,
    preferThreeD: false,
    // Project-level commissioning flags (app_config)
    hasInsolationCommissioned: false,
    hasArrangeCommissioned: false,
    hasAxonometricCommissioned: false,
    hasHotspotCommissioned: false,
    // Dataset-level checks (ANY product)
    datasetHasProductWithInsolation: false,
    datasetHasProductWithHotspots: false,
    datasetHasProductWithAxonometric: false,
    datasetHasProductWithCompartmentVariants: false,
    enabledModes: [],
    defaultMode: null,
    availableColorSchemes: [],
    modes: {
      availableModes: [],
      activeMode: null,
      defaultMode: null,
      setActiveMode: () => {},
      resetActiveMode: () => {},
    },


    LIST FINAL CAPABILITIES DISTILLED FROM ABOVE TO IMPLEMENT:

    // Dataset structure
    hasMultipleAreas,
    hasMultipleBuildings,
    hasMultipleGates,
    hasMultipleTiers,
    hasMultiLevelProducts,

    // Supporting resources
    compartmentTypesAvailable,
    hasInteriorAssets,

    // Project commissioning
    insolationCommissioned,
    compartmentVariantsCommissioned,
    axonometricCommissioned,
    interiorsCommissioned,

    // Dataset support
    hasProductsWithInsolation,
    hasProductsWithCompartmentVariants,
    hasProductsWithAxonometric,
    hasProductsWithHotspots,

    // Effective Inventory-level availability
    insolationAvailable,
    compartmentVariantsAvailable,
    axonometricAvailable,
    hotspotsAvailable,

  */

	// ── Capabilities ─────────────────────────────────────────────────────────
  const capabilities = useMemo(
    () =>
      deriveInventoryCapabilities({
        buildings: enhancedBuildings,
        commissioning,
        compartmentsStatus: compartmentsResource.status,
        compartmentTypes: compartmentsResource.data,
        interiors: interiorsResource.data,
        interiorsStatus: interiorsResource.status,
        products: displayableProducts,
      }),
    [
      commissioning,
      compartmentsResource,
      displayableProducts,
      enhancedBuildings,
      interiorsResource,
    ],
  )
  
  // ── Color mode (shared between TiersView / ModelView) ──────────────────────
  const { activeColorModes, defaultColorMode } = deriveColorModes(panelSettings)
  
  // const {
  //   activeColorModes,
  //   defaultColorMode,
  //   reservedUnitsAccessible,
  //   soldUnitsAccessible,
  // } = appCapabilities;

  const { colorMode, setColorMode, availableColorModes } = useColorMode({ //TODO move hook to inventory domain
    activeColorModes,
    defaultColorMode,
  });
  
  const colorModeDeciles = useMemo(() => {
    if (colorMode !== COLOR_MODE.COMPARE) return null;
    return buildProductAreaDeciles(
      displayableProducts.filter((p) => isDisplayable(p.visibility)),
    );
  }, [colorMode, displayableProducts]);

  // ── Defaults ─────────────────────────────────────────────────────────
	// ── Queries ─────────────────────────────────────────────────────────
	// ── Actions ─────────────────────────────────────────────────────────

  const soldOutBuildingsClickEnabled = panelSettings?.model_load_config?.sold_out_building_outlines_active !== false //move to appCapabilities??
  const soldOutTiersClickEnabled = panelSettings?.model_load_config?.sold_out_tiers_outlines_active !== false //move to appCapabilities??

  const pricesVisibility = panelSettings?.prices_visibility ?? {}
  const pricesVisible =         pricesVisibility?.prices_visible          !== false;
  const soldPricesVisible =     pricesVisibility?.prices_sold_visible     !== false;
  const reservedPricesVisible = pricesVisibility?.prices_reserved_visible !== false;

  const modelLoadConfig = panelSettings?.model_load_config
  const highlightSoldInRed = modelLoadConfig?.highlight_sold_in_red ?? true

  const productAccessibility = panelSettings?.product_sheet_accessibility
  const reservedUnitsAccessible   = productAccessibility?.reserved_products.is_accessible ?? true //CAPABILITY?
  const reservedUnitsPlanVisible  = productAccessibility?.reserved_products.plan_visible ?? true
  const soldUnitsAccessible       = productAccessibility?.sold_products.is_accessible ?? true //CAPABILITY?
  const soldUnitsPlanVisible      = productAccessibility?.sold_products.plan_visible ?? true
  const allowSoldUnitQuery        = productAccessibility?.sold_products.allow_inquiry ?? false //CAPABILITY?


	// ── Context status ─────────────────────────────────────────────────────────
  const dataStatuses = {
    buildingsStatus: buildingsResource.status,
    compartmentsStatus: compartmentsResource.status,
    interiorsStatus: interiorsResource.status,
    productsStatus: productsResource.status,
  }
  const providerStatus = deriveInventoryStatus(dataStatuses)
	
  // ── Context value ─────────────────────────────────────────────────────────
  const value = useMemo(
    () => ({
      actions: {
        setColorMode,
      },
      capabilities,
      data: {
        buildings: enhancedBuildings,
        compartments: compartmentsResource.data,
        displayableProducts,
        interiors: interiorsResource.data,
        products: productsResource.data, //equals allProducts  //TODO: this context should export non main category products (parking, storage units etc)
        tiers,
      },
      //defaults:{
      //  colorMode: defaultColorMode,
      //productCategory: defaultProductCategory,
      // }
      presentation: {
        availableColorModes, //old activeColorModes
        colorModeDeciles,
        highlightSoldInRed,
        pricesVisible,
        reservedPricesVisible,
        reservedUnitsPlanVisible,
        soldOutBuildingsClickEnabled,
        soldOutTiersClickEnabled,
        soldPricesVisible,
        soldUnitsPlanVisible,

      },
      state: {
        colorMode
      },
      status: {
        buildings: {
          status: buildingsResource.status,
          error: buildingsResource.error,
        },
        compartments: {
          status: compartmentsResource.status,
          error: compartmentsResource.error,
        },
        interiors: {
          status: interiorsResource.status,
          error: interiorsResource.error,
        },
        products: {
          status: productsResource.status,
          error: productsResource.error,
        },
        overall: providerStatus
      }
    }),
    [
      buildingsResource,
      capabilities,
      compartmentsResource,
      displayableProducts,
      enhancedBuildings,
      interiorsResource,
      productsResource,
      tiers
    ]
  )

  return <InventoryContext.Provider value={value}>{children}</InventoryContext.Provider>
}

// ── Hook ──────────────────────────────────────────────
/**
 * @typedef {'loading'|'ready'|'error'} InventoryFetchStatus
 */

/**
 * State of one independently loaded Inventory resource.
 *
 * @typedef {Object} InventoryResourceStatus
 * @property {InventoryFetchStatus} status - Current resource lifecycle state.
 * @property {Error|null} error - Loading or validation error, or `null` when none occurred.
 */

/**
 * Data model exposed by the Inventory domain.
 *
 * @typedef {Object} InventoryData
 * @property {Object[]} buildings - Buildings enriched with CMS phasing data and tier-derived flags.
 * @property {Record<string, unknown>} compartments - Compartment-type dictionary keyed by source identifiers.
 * @property {Object[]} interiorsData - Interior-design data; populated when the optional resource is integrated.
 * @property {Object[]} products - Products merged from the configured main product categories.
 * @property {Object[]} tiers - Product-relevant tiers enriched with CMS tier settings and availability flags.
 */

/**
 * Per-resource loading states exposed by the Inventory domain.
 *
 * @typedef {Object} InventoryStatus
 * @property {InventoryResourceStatus} buildings - Required buildings resource state.
 * @property {InventoryResourceStatus} compartments - Optional compartments resource state.
 * @property {InventoryResourceStatus} products - Required products resource state.
 */

/**
 * Public value of `InventoryContext` during the current migration stage.
 *
 * @typedef {Object} InventoryContextValue
 * @property {InventoryData} data - Loaded and derived Inventory data.
 * @property {InventoryStatus} status - Independent lifecycle state for each Inventory resource.
 */

/**
 * Returns the public Inventory domain state.
 *
 * The hook must be called under `InventoryContextProvider`. Resource data is
 * grouped under `data`, while loading and error information is grouped under
 * `status`, so consumers do not depend on raw configuration or fetch details.
 *
 * @returns {InventoryContextValue} Loaded Inventory data and per-resource statuses.
 * @throws {Error} When called outside `InventoryContextProvider`.
 */
export function useInventory() {
  const ctx = useContext(InventoryContext)
  if (!ctx) throw new Error('useInventory must be used within InventoryContextProvider')
  return ctx
}

